import dayjs, { Dayjs, isDayjs } from "dayjs";
import CustomParseFormat from "dayjs/plugin/customParseFormat";
import Timezone from "dayjs/plugin/timezone";
import Utc from "dayjs/plugin/utc";
import {
  buildMessage,
  isDecimal,
  ValidateBy,
  ValidationOptions,
} from "class-validator";
import { Transform } from "class-transformer";
import DecimalType from "decimal.js";

dayjs.extend(CustomParseFormat);
dayjs.extend(Utc);
dayjs.extend(Timezone);

function isValidDayjsPredicate(value: unknown): value is Dayjs {
  return isDayjs(value) && value.isValid();
}

export function isValidBigIntPredicate(value: unknown): value is bigint {
  return typeof value === "bigint";
}

export function minDecimalPredicate(value: unknown, min: DecimalType): boolean {
  return value instanceof DecimalType && value.greaterThanOrEqualTo(min);
}

export function MinDecimal(min: DecimalType, options?: ValidationOptions): PropertyDecorator {
  return ValidateBy(
    {
      name: "minDecimal",
      validator: {
        validate: (value): boolean => minDecimalPredicate(value, min),
        defaultMessage: buildMessage(
          (eachPrefix) => `${eachPrefix}$property must be greater than or equal to ${min.toString()}`,
          options,
        ),
      },
    },
  );
}

export function IsBigInt(options?: ValidationOptions): PropertyDecorator {
  return ValidateBy(
    {
      name: "isBigInt",
      validator: {
        validate: (value): boolean => isValidBigIntPredicate(value),
        defaultMessage: buildMessage(
          (eachPrefix) => `${eachPrefix}$property must be a valid bigint`,
          options,
        ),
      },
    },
  );
}

export function IsValidDayjs(options?: ValidationOptions): PropertyDecorator {
  return ValidateBy(
    {
      name: "isValidDayjs",
      validator: {
        validate: (value): boolean => isValidDayjsPredicate(value),
        defaultMessage: buildMessage(
          (eachPrefix) => `${eachPrefix}$property must be a valid ISO-8601 date string`,
          options,
        ),
      },
    },
    options,
  );
}

/** Parses non-blank strings as "YYYY-MM-DD" dates in the server's timezone.
 * Anything else (including undefined, null and blank strings) is returned as is,
 * so that the validators decide whether it is acceptable:
 * {@link IsValidDayjs} rejects it, while `@IsOptional` still accepts undefined and null. */
export function TransformToDayjsDate() {
  return Transform(({ value }) => {
    if (typeof value !== "string" || value.trim() === "") {
      return value;
    }

    // dayjs.tz throws instead of returning an invalid date if it can't parse its input,
    // so we parse without the timezone first and hand the invalid date to the validators
    const parsed = dayjs(value, "YYYY-MM-DD");

    return parsed.isValid()
      ? dayjs.tz(value, "YYYY-MM-DD", dayjs.tz.guess())
      : parsed;
  });
}

/** @param [decimalPlaces=2] The number of decimal places to keep.
 * Set to null if you don't want rounding to be performed.
 * Note that even if this is set to null, the value will still be rounded
 * if it exceeds decimal.js's maximum precision
 * (though you'll probably never care about that with the kind of numbers we usually deal with).
 * If you want to use a different precision, pass a different constructor.
 * @param [roundingMode=DecimalType.ROUND_HALF_UP] The rounding mode to use.
 * Uses {@link DecimalType.ROUND_HALF_UP} by default.
 * @param [acceptNonFinite=false] If true, non-finite numbers (e.g. NaN or Infinity)
 * will be converted to the equivalent Decimal value.
 * If false, non-finite numbers will be converted to null.
 * @param [decimalTypeCreator=value => new DecimalType(value)] A function that takes a value
 * and returns a DecimalType instance.
 * You can use {@link DecimalType.clone} to define a new constructor if you want to
 * have a different precision or rounding mode.
 * @returns The value parsed as a decimal.js DecimalType instance, rounded if necessary.
 * If the value is a blank string, returns null.
 * If the value is not finite (i.e. NaN or Infinity) and acceptNonFinite is false,
 * returns null.
 * If the value is not a valid decimal string or number,
 * returns the value as is. */
export function TransformToDecimal({
  decimalPlaces = 2,
  roundingMode = DecimalType.ROUND_HALF_UP,
  acceptNonFinite = false,
  decimalTypeCreator = (value) => (new DecimalType(value)),
}: {
  decimalPlaces?: number | null;
  roundingMode?: DecimalType.Rounding;
  acceptNonFinite?: boolean;
  decimalTypeCreator?: (value: DecimalType.Value) => DecimalType;
} = {}) {
  return Transform(({ value }) => {
    let parsed: DecimalType | undefined;
    if (
      typeof value === "string"
    ) {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        return null;
      }
      if (!isDecimal(value)
        && trimmedValue !== "NaN"
        && trimmedValue !== "Infinity"
        && trimmedValue !== "-Infinity") {
        return value;
      }
      parsed = decimalTypeCreator(trimmedValue);
    } else if (
      typeof value === "number" ||
      (typeof value === "object" && DecimalType.isDecimal(value))
    ) {
      parsed = decimalTypeCreator(value);
    } else {
      return value;
    }
    if (!acceptNonFinite && !parsed.isFinite()) {
      return null;
    }
    if (decimalPlaces !== null) {
      parsed = parsed.toDecimalPlaces(decimalPlaces, roundingMode);
    }
    return parsed;
  });
}

/**
 * @returns Parsed as a bigint.
 */
export function TransformToBigInt() {
  return Transform(({ value }) => {
    try {
      return BigInt(value);
    } catch {
      // Invalid bigint, return the value as is
      // This will be caught by the class-validator (isBigInt) and converted to a validation error
      return value;
    }
  });
}

/**
 * @returns Parsed as a number. Only used for query params.
 */
export function TransformToNum() {
  return Transform(({ value }) => Number(value));
}
/**
 * @returns Parsed as a boolean. Only used for query params.
 */
export function TransformToBool() {
  return Transform(({ value }) => value === "true");
}

export function TransformPostcode() {
  return Transform(({ value }) => {
    if (typeof value !== "string") {
      return value;
    }

    const transformed = value.toUpperCase().replace(/\s+/g, "");
    if (transformed.length < 4) {
      return transformed;
    }

    return `${transformed.slice(0, transformed.length - 3)} ${transformed.slice(transformed.length - 3, transformed.length)}`;
  });
}

/**
 * @param emptyToNull if true, then an empty value will be converted to null
 * @returns If the value is a string, returns the trimmed value.
 * @returns If the transformed value is empty and emptyToNull is set, returns null.
 * @returns If the value is not a string, returns the value as is, without any modification.
 */
export function TransformTrim(emptyToNull = false) {
  return Transform(({ value }) => {
    if (typeof value !== "string") {
      return value;
    }

    const trimmed = value.trim();

    if (emptyToNull && trimmed.length <= 0) {
      return null;
    }

    return trimmed;

  });
}

/**
 * @returns If the value is a string, the uppercased value.
 * @returns If the value is not a string, the value as is, without any modification.
 */
export function TransformUppercase() {
  return Transform(({ value }) => (typeof value === "string" ? value.toUpperCase() : value));
}
/**
 * @returns If the value is a string, the lowercased value.
 * @returns If the value is not a string, the value as is, without any modification.
 */
export function TransformLowercase() {
  return Transform(({ value }) => (typeof value === "string" ? value.toLowerCase() : value));
}

/** If given a non-array value, converts it into an array.
 * This is useful when you want to consistently handle query parameters.
 * A single query parameter is returned as a string
 * while multiple query parameters with the same name are returned as an array.
 * By using this annotation, we can be certain that query params will always be an array,
 * and we don't have to check them with Array.isArray all the time.
 * @param transformer A function that transforms the value before wrapping it in an array.
 * @returns If the value is an array, returns it as is
 * @returns If the value is null or undefined, returns it as is
 * @returns For all other cases, wraps the value in an array and returns it */
export function TransformArray(
  transformer?: (value: unknown) => unknown,
) {
  return Transform(({ value }) => {
    if (value === null || value === undefined) {
      return value;
    }
    if (!Array.isArray(value)) {
      if (transformer) {
        return [ transformer(value) ];
      }
      return [ value ];
    }
    if (transformer) {
      return value.map(transformer);
    }
    return value;

  });
}
